import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tag } from "antd";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable/index";
import Toasts from "../../../core/common/toast";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import {
  getLoginLogs,
  resetAll,
} from "../../../core/data/redux/loginLogsSlice";
import { formatDateTime } from "../../../utils/dateUtils";
import { debounce } from "lodash";
import { useCallback } from "react";

const routes = all_routes;

interface LoginLogData {
  id: number;
  userId: number;
  username: string;
  ipAddress: string;
  status: string;
  responseMessage: string | null;
  createdAt: string;
  agent: string | null;
}

const LoginLogs: React.FC = () => {
  const dispatch = useDispatch();
  const loginLogsState = useSelector((state: any) => state.loginLogsState);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchText, setSearchText] = useState("");

  const loginLogsData = loginLogsState?.loginLogsData || null;

  const fetchData = (page = pagination.current, limit = pagination.pageSize, username = searchText) => {
    const payload = {
      page,
      limit,
      ...(username && { username }),
    };
    dispatch(getLoginLogs(payload));
  };

  useEffect(() => {
    fetchData();
  }, [pagination.pageSize]);

  useEffect(() => {
    try {
      if (loginLogsData && loginLogsData.data) {
        const apiResponse = loginLogsData.data;
        const total = apiResponse.total || 0;
        setPagination((prev) => ({
          ...prev,
          total: total,
        }));
      }
    } catch (error) {
      // Handle pagination error silently
    }
  }, [loginLogsData]);

  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      render: (_: any, __: any, index: number) => (
        <div>{(pagination.current - 1) * pagination.pageSize + index + 1}</div>
      ),
      width: 80,
    },
    {
      title: "User ID",
      dataIndex: "userId",
      render: (userId: number) => (
        <div style={{ fontWeight: 500 }}>
          {userId || "-"}
        </div>
      ),
      sorter: (a: LoginLogData, b: LoginLogData) => a.userId - b.userId,
      width: 100,
    },
    {
      title: "Username",
      dataIndex: "username",
      render: (username: string) => (
        <div style={{ fontWeight: 500, color: "#1890ff" }}>
          {username || "-"}
        </div>
      ),
      sorter: (a: LoginLogData, b: LoginLogData) => (a.username || "").localeCompare(b.username || ""),
      width: 150,
    },
    {
      title: "IP Address",
      dataIndex: "ipAddress",
      render: (ipAddress: string) => (
        <div style={{ fontFamily: "monospace", fontSize: "12px" }}>
          {ipAddress.replace(/^::ffff:/, "") || "-"}
        </div>
      ),
      width: 150,
    },
    {
      title: "User Agent",
      dataIndex: "agent",
      render: (agent: string) => (
        <div style={{ fontFamily: "monospace", fontSize: "12px" }} className="long-text">
          {agent || "-"}
        </div>
      ),
      width: 200,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: string) => {
        const getStatusColor = (stat: string) => {
          switch (stat?.toUpperCase()) {
            case "SUCCESS":
              return "success";
            case "FAILED":
            case "FAILURE":
              return "error";
            case "BLOCKED":
              return "warning";
            default:
              return "default";
          }
        };

        return (
          <Tag color={getStatusColor(status)} style={{ fontWeight: 500 }}>
            {status || "UNKNOWN"}
          </Tag>
        );
      },
      width: 120,
    },
    {
      title: "Response Message",
      dataIndex: "responseMessage",
      render: (responseMessage: string) => (
        <div style={{
          maxWidth: "200px",
          wordWrap: "break-word",
          fontSize: "12px",
          color: responseMessage ? "#666" : "#ccc"
        }}>
          {responseMessage || "No message"}
        </div>
      ),
      width: 200,
    },
    {
      title: "Login Time",
      dataIndex: "created_at",
      render: (date: string) => (
        <div style={{ fontSize: "12px" }}>
          {date ? formatDateTime(date) : "N/A"}
        </div>
      ),
      sorter: (a: LoginLogData, b: LoginLogData) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      width: 180,
    },
  ];

  const debouncedFetch = useCallback(
    debounce((resetPagination: boolean, value: string) => {
      if (resetPagination) {
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchData(1, pagination.pageSize, value);
      } else {
        fetchData(pagination.current, pagination.pageSize, value);
      }
    }, 500),
    [pagination.pageSize, pagination.current]
  );

  const handleSearch = (value: string) => {
    setSearchText(value);
    debouncedFetch(true, value);
  };

  const handlePagination = (paginationConfig: any) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
      total: paginationConfig.total,
    });
    fetchData(paginationConfig.current, paginationConfig.pageSize, searchText);
  };

  const getTableData = () => {
    if (loginLogsData?.data?.data && Array.isArray(loginLogsData.data.data)) {
      return loginLogsData.data.data;
    }
    return [];
  };

  const tableData = getTableData();

  return (
    <div className="page-wrapper">
      <Toasts
        show={loginLogsState.loading === "rejected"}
        message={loginLogsState.error}
        title={"Failed"}
        type={"danger"}
      />

      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Login Logs</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Logs</li>
                <li className="breadcrumb-item active" aria-current="page">
                  Login Logs
                </li>
              </ol>
            </nav>
          </div>
          <div className="head-icons ms-2 d-flex align-items-center gap-2">
            <CollapseHeader />
          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap">
            <h5 className="card-title mb-0">
              <i className="ti ti-login me-2" />
              Login History ({pagination.total} total)
            </h5>
          </div>
          <div className="card-body p-0 py-3">
            <Table
              columns={columns}
              dataSource={tableData}
              Selection={false}
              paginationConfig={pagination}
              handlePagination={handlePagination}
              loading={loginLogsState.loading === "pending"}
              handleSearch={handleSearch}
              searchValue={searchText}
            />
          </div>
        </div>
      </div>

      <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
        <p className="mb-0">DMRC Admin Panel, All Rights Reserved.</p>
      </div>
    </div>
  );
};

export default LoginLogs;
